import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Search,
    Save,
    AlertTriangle,
    Lock,
} from 'lucide-react';
import {
    Alert,
    AlertDescription,
    AlertTitle,
    Badge,
    Button,
    Card,
    Input,
    Label,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui';
import { cn } from '@/lib/utils';
import {
    BILLING_DOC_TYPES,
    BILLING_TAX_CONDITIONS,
    TUSFACTURAS_PROVINCES,
    digitsOnly,
    validateProfile,
} from '../../../helpers/billingProfile';

const emptyProfile = {
    businessName: '',
    docType: 'CUIT',
    docNumber: '',
    taxCondition: 'RESPONSABLE INSCRIPTO',
    address: '',
    city: '',
    province: 'CIUDAD AUTONOMA DE BUENOS AIRES',
    zipCode: '',
    billingEmail: '',
};

// Etiquetas legibles para el aviso de confirmación al pisar datos con ARCA.
const FIELD_LABELS = {
    businessName: 'Razón social',
    taxCondition: 'Condición IVA',
    address: 'Domicilio',
    city: 'Localidad',
    province: 'Provincia',
    zipCode: 'Código postal',
};

// Estilo de error: borde rojo sólido (sin alpha, que los tokens no soportan).
const errorInput = 'border-danger focus-visible:border-danger';

// Campo con label ARRIBA (sin floating labels que recortan el borde).
function Field({ id, label, required, error, hint, className, children }) {
    return (
        <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
            <span className="flex items-center gap-1">
                <Label htmlFor={id} className="text-[13px] font-medium text-foreground">{label}</Label>
                {required ? <span aria-hidden="true" className="text-[13px] leading-none text-danger">*</span> : null}
            </span>
            {children}
            {error
                ? <p className="text-xs font-medium text-danger">{error}</p>
                : hint
                    ? <p className="text-xs text-muted-foreground">{hint}</p>
                    : null}
        </div>
    );
}

Field.propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    required: PropTypes.bool,
    error: PropTypes.string,
    hint: PropTypes.string,
    className: PropTypes.string,
    children: PropTypes.node,
};

Field.defaultProps = {
    required: false,
    error: undefined,
    hint: undefined,
    className: undefined,
    children: null,
};

function BillingProfileForm({ profile, completeness, onSave, onAfipLookup, blockedCount }) {
    const [values, setValues] = useState(emptyProfile);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    // Campos que el usuario tocó a mano: ARCA no los pisa sin confirmación.
    const [touched, setTouched] = useState(() => new Set());

    useEffect(() => {
        setValues({ ...emptyProfile, ...(profile || {}) });
        setTouched(new Set());
    }, [profile]);

    const setField = (field, value) => {
        setValues((current) => ({ ...current, [field]: value }));
        setTouched((current) => {
            const next = new Set(current);
            next.add(field);
            return next;
        });
    };

    const handleSave = async () => {
        const nextErrors = validateProfile(values);
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0) return;
        setSaving(true);
        try {
            await onSave({ ...values, docNumber: digitsOnly(values.docNumber) });
        } finally {
            setSaving(false);
        }
    };

    const handleAfipLookup = async () => {
        if (!onAfipLookup) return;
        const data = await onAfipLookup(digitsOnly(values.docNumber));
        if (!data) return;
        const incoming = {
            businessName: data.businessName || data.razon_social,
            docType: data.docType || values.docType,
            docNumber: data.docNumber || values.docNumber,
            taxCondition: data.taxCondition || data.condicion_impositiva,
            address: data.address || data.direccion,
            city: data.city || data.localidad,
            province: data.province || data.provincia,
            zipCode: data.zipCode || data.codigopostal,
            // El padrón no es la fuente autoritativa del email de entrega.
            // Si el usuario ya lo escribió, nunca lo pisamos ni pedimos confirmación.
            billingEmail: values.billingEmail || data.billingEmail,
            source: data.source || 'afip-prefill',
        };
        // Detectamos conflictos: campos que el usuario ya editó y que ARCA
        // devuelve con un valor distinto. Nunca los pisamos sin confirmar.
        const conflicts = Object.keys(incoming).filter((field) => (
            incoming[field]
            && touched.has(field)
            && values[field]
            && values[field] !== incoming[field]
        ));
        let overwriteEdited = false;
        if (conflicts.length > 0) {
            const labels = conflicts.map((field) => FIELD_LABELS[field]).join(', ');
            // eslint-disable-next-line no-alert
            overwriteEdited = window.confirm(`Ya editaste: ${labels}. ¿Reemplazar con los datos de ARCA?`);
        }
        const next = { ...values };
        Object.keys(incoming).forEach((field) => {
            if (!incoming[field]) return;
            const isConflict = conflicts.includes(field);
            if (isConflict && !overwriteEdited) return;
            next[field] = incoming[field];
        });
        setValues(next);
    };

    // Progreso "vivo" del perfil: cuántos campos requeridos ya son válidos.
    // Solo alimenta el indicador visual; la validación real sigue en handleSave.
    const liveErrors = validateProfile(values);
    const requiredInputs = ['docNumber', 'businessName', 'address', 'billingEmail'];
    const totalReq = requiredInputs.length;
    const doneReq = requiredInputs.filter((key) => !liveErrors[key]).length;
    const serverComplete = touched.size === 0 && Boolean(completeness && completeness.ok);
    const complete = doneReq === totalReq || serverComplete;
    const pct = complete ? 100 : Math.round((doneReq / totalReq) * 100);

    const afipDisabled = values.docType !== 'CUIT' || digitsOnly(values.docNumber).length !== 11;

    return (
        <Card className="p-5 text-left sm:p-6">
            {blockedCount > 0 && (
                <Alert variant="warning" className="mb-5">
                    <AlertTriangle />
                    <AlertTitle>Datos fiscales incompletos</AlertTitle>
                    <AlertDescription>
                        Completá tus datos fiscales para recibir tu factura. Tenés {blockedCount} facturas esperando.
                    </AlertDescription>
                </Alert>
            )}

            <div className="mb-5 flex items-start justify-between gap-3">
                <div className="space-y-1">
                    <h2 className="text-lg font-bold leading-tight tracking-[-0.01em] text-foreground">Datos de facturación</h2>
                    <p className="text-[13px] text-muted-foreground">Con estos datos emitimos tus comprobantes fiscales.</p>
                </div>
                <Badge variant={complete ? 'success' : 'warning'} size="lg" className="shrink-0 tabular-nums">
                    {complete ? 'Completo' : `${pct}%`}
                </Badge>
            </div>

            <div className="grid grid-cols-1 gap-x-3 gap-y-3.5 sm:grid-cols-12">
                <Field id="billing-docType" label="Tipo" error={errors.docType} className="sm:col-span-3">
                    <Select value={values.docType} onValueChange={(value) => setField('docType', value)}>
                        <SelectTrigger id="billing-docType"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {BILLING_DOC_TYPES.map((option) => (
                                <SelectItem key={option} value={option}>
                                    {option === 'CUIT' ? 'CUIT / CUIL' : option}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </Field>

                <Field id="billing-docNumber" label="Documento" required error={errors.docNumber} className="sm:col-span-9">
                    <div className="flex flex-wrap items-center gap-2">
                        <Input
                            id="billing-docNumber"
                            value={values.docNumber}
                            onChange={(e) => setField('docNumber', e.target.value)}
                            inputMode="numeric"
                            placeholder="CUIT/CUIL o DNI, solo números"
                            className={cn('min-w-[9rem] flex-1', errors.docNumber && errorInput)}
                        />
                        <Button type="button" variant="outline" onClick={handleAfipLookup} disabled={afipDisabled} className="shrink-0">
                            <Search /> Buscar en ARCA
                        </Button>
                    </div>
                </Field>

                <Field id="billing-taxCondition" label="Condición IVA" error={errors.taxCondition} className="sm:col-span-5">
                    <Select value={values.taxCondition} onValueChange={(value) => setField('taxCondition', value)}>
                        <SelectTrigger id="billing-taxCondition"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {BILLING_TAX_CONDITIONS.map((option) => (<SelectItem key={option} value={option}>{option}</SelectItem>))}
                        </SelectContent>
                    </Select>
                </Field>

                <Field id="billing-businessName" label="Razón social" required error={errors.businessName} className="sm:col-span-7">
                    <Input id="billing-businessName" value={values.businessName} onChange={(e) => setField('businessName', e.target.value)} placeholder="Mi Empresa S.A." className={cn(errors.businessName && errorInput)} />
                </Field>

                <Field id="billing-address" label="Domicilio" required error={errors.address} className="sm:col-span-6">
                    <Input id="billing-address" value={values.address} onChange={(e) => setField('address', e.target.value)} placeholder="Av. Corrientes 1234" className={cn(errors.address && errorInput)} />
                </Field>

                <Field id="billing-city" label="Localidad" className="sm:col-span-3">
                    <Input id="billing-city" value={values.city} onChange={(e) => setField('city', e.target.value)} placeholder="CABA" />
                </Field>

                <Field id="billing-zipCode" label="Código postal" className="sm:col-span-3">
                    <Input id="billing-zipCode" value={values.zipCode} onChange={(e) => setField('zipCode', e.target.value)} inputMode="numeric" placeholder="1043" />
                </Field>

                <Field id="billing-province" label="Provincia" className="sm:col-span-5">
                    <Select value={values.province} onValueChange={(value) => setField('province', value)}>
                        <SelectTrigger id="billing-province"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {TUSFACTURAS_PROVINCES.map((option) => (<SelectItem key={option} value={option}>{option}</SelectItem>))}
                        </SelectContent>
                    </Select>
                </Field>

                <Field id="billing-billingEmail" label="Email de facturación" required error={errors.billingEmail} className="sm:col-span-7">
                    <Input id="billing-billingEmail" type="email" value={values.billingEmail} onChange={(e) => setField('billingEmail', e.target.value)} placeholder="facturacion@miempresa.com" className={cn(errors.billingEmail && errorInput)} />
                </Field>
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Lock className="size-3.5 shrink-0" />
                    Tus datos se guardan de forma segura.
                </p>
                <Button type="button" onClick={handleSave} disabled={saving} className="w-full sm:w-auto">
                    <Save /> Guardar
                </Button>
            </div>
        </Card>
    );
}

BillingProfileForm.propTypes = {
    profile: PropTypes.object,
    completeness: PropTypes.object,
    onSave: PropTypes.func.isRequired,
    onAfipLookup: PropTypes.func,
    blockedCount: PropTypes.number,
};

BillingProfileForm.defaultProps = {
    profile: {},
    completeness: { ok: false },
    onAfipLookup: null,
    blockedCount: 0,
};

export default BillingProfileForm;
